import { RealtimeClient, RealtimeUtils } from '@openai/realtime-api-beta';
import { AudioIO } from '../utils/audio';
import { Agent, TextIO } from '../agent/agent';
import { CONFIG } from '../config';

import { encodePCM16ToBase64 } from '../utils/audio';

const LOCAL_RELAY_SERVER_URL: string =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

// old model: 'gpt-4o-realtime-preview-2024-10-01'
const realtimeModel = '$42069-per-minute'; //'gpt-4o-mini-realtime-preview-2024-12-17';

export class Session {
  private client: RealtimeClient;
  private audioIO: AudioIO;
  private textIO: TextIO;
  private agent: Agent;
  private isConnectedPromise = new Promise(resolve => resolve(false));
  private isMuted: boolean = false;
  private onConnectionStatusChange?: (connected: boolean) => void;
  private timeoutId?: NodeJS.Timeout;

  private startTime: string = new Date().toISOString();

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  constructor(apiKey: string) {
    const realtimeClientConfig = LOCAL_RELAY_SERVER_URL
      ? { url: LOCAL_RELAY_SERVER_URL }
      : {
        apiKey: apiKey,
        dangerouslyAllowAPIKeyInBrowser: true,
      };
    this.client = new RealtimeClient(realtimeClientConfig);
    this.client.appendInputAudio = (arrayBuffer) => {
      if (arrayBuffer.byteLength > 0) {
        const audio = encodePCM16ToBase64(new Int16Array(arrayBuffer));
        this.client.realtime.send('input_audio_buffer.append', { audio });
        this.client.inputAudioBuffer = RealtimeUtils.mergeInt16Arrays(
          this.client.inputAudioBuffer,
          arrayBuffer,
        );
      }
      return true;
    }

    const originalClientConnect = this.client.realtime.connect;
    this.client.realtime.connect = async function ({ model } = { model: realtimeModel }) {
      return originalClientConnect.call(this, { model });
    }

    const originalClientDisconnect = this.client.realtime.disconnect;
    this.client.realtime.disconnect = (ws) => {
      const isDisconnected = originalClientDisconnect.call(this.client.realtime, ws);
      if (isDisconnected && this.onConnectionStatusChange) {
        this.onConnectionStatusChange(false);
      }
      return isDisconnected;
    }

    this.audioIO = new AudioIO();
    this.textIO = { setItems: (items) => { } };
    this.agent = new Agent(this.client, this.audioIO, this.textIO);

    // Connect audio input/output in the beginning to be prepared for audio recording
    // TODO: we should use a loading spinner here, prevent user from interacting until we're ready
    this.audioIO.connect();
  }

  private startTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.disconnect();
      if (this.onConnectionStatusChange) {
        this.onConnectionStatusChange(false);
      }
    }, CONFIG.CONVERSATION_TIMEOUT_MS);
  }

  private resetTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.startTimeout();
  }

  async initAudioplayer() {
    // The AudioContext was not allowed to start.
    // It must be resumed (or created) after a user gesture on the page.
    // https://goo.gl/7K7WLu
    await this.audioIO.initAudioPlayer();
  }

  setTextIO(textIO: TextIO | null) {
    if (!textIO) {
      textIO = { setItems: (items) => { } };
    }
    this.textIO = textIO;
    this.agent.setTextIO(textIO);
  }

  async changeTurnEndType(value: string, readyCallback: () => void) {
    if (value === 'none') {
      await this.audioIO.stopRecording();
    }
    this.agent.updateTurnDetection(value);
    if (value === 'server_vad') {
      await this.audioIO.startRecording(data => this.client.appendInputAudio(data));
    }
    readyCallback();
    await this.connect(() => { });
  };

  setConnectionStatusCallback(callback: (connected: boolean) => void) {
    this.onConnectionStatusChange = callback;
  }

  async connect(readyCallback: () => void) {
    if (await this.isConnectedPromise) {
      return;  // already connected
    }
    let connectionResolver: (value: boolean) => void = (_) => { };
    this.isConnectedPromise = new Promise(resolve => {
      connectionResolver = resolve;
    });

    // Set the start time of the session
    this.startTime = new Date().toISOString();

    // Connect to microphone and speaker. Connect to realtime API
    await Promise.all([this.audioIO.connect(), this.client.realtime.connect()]);
    this.startTimeout();
    // NOTE: this is our customized event
    this.agent.updateSession();
    this.agent.setItems(this.client.conversation.getItems());
    
    readyCallback();
    connectionResolver(true);
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(true);
    }
    // commit pending audio to avoid losing audio
    this.audioIO.commitRecording(data => this.client.appendInputAudio(data));

    if (this.client.getTurnDetectionType() === 'server_vad') {
      await this.audioIO.startRecording(data => this.client.appendInputAudio(data));
    }
  }

  getStartTime() {
    return this.startTime;
  }

  async interruptAudioPlayback() {
    const client = this.client;
    const audioIO = this.audioIO;
    const trackSampleOffset = await audioIO.interruptAudioPlayback();
    if (client.isConnected()) {
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        client.cancelResponse(trackId, offset);
      }
    }
  }

  async sendUserText(text: string) {
    // this.resetTimeout();
    await this.connect(() => { });
    await this.interruptAudioPlayback();
    this.client.sendUserMessageContent([
      {
        type: 'input_text',
        text: text,
      },
    ]);
  }

  async startRecording() {
    // this.resetTimeout();
    const client = this.client;
    const audioIO = this.audioIO;
    try {
      await this.interruptAudioPlayback();
      await audioIO.startRecording(data => client.appendInputAudio(data));
      await this.connect(() => { });
    } catch (error) {
      console.error('Error during recording:', error);
    }
  }

  async stopRecording() {
    await this.audioIO.stopRecording();
    await this.connect(() => { });
    this.client.createResponse();
  }

  async disconnect() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this.client.disconnect();
    await this.audioIO.disconnect();
    this.isConnectedPromise = new Promise(resolve => resolve(false));
  }

  getInputFrequencyData() {
    return this.audioIO.getInputFrequencyData();
  }

  getOutputFrequencyData() {
    return this.audioIO.getOutputFrequencyData();
  }

  deleteItem(id: string) {
    this.agent.deleteItem(id);
  }

  setMuted(muted: boolean) {
    this.isMuted = muted;
    this.audioIO.setMuted(this.isMuted);
  }

  getMuted() {
    return this.isMuted;
  }
}
